// Screen-space geometry of scene elements, for selection outlines, hit-testing and drag conversion.
// Uses the exact same camera/model matrices as the renderer so overlays stay glued to the picture in 3D.
import { evaluate } from "../../../engine/src/evaluate.ts";
import { cameraMatrices, modelMatrix, multiply, type Mat4, type Vec3 } from "../../../engine/src/math.ts";
import type { DrawableElement, Project } from "../../../engine/src/types.ts";

export interface Pt { x: number; y: number }
export interface Projected { id: string; hull: Pt[]; center: Pt; depth: number; pxPerUnitX: number; pxPerUnitY: number }
export type Measure = (el: DrawableElement) => { width: number; height: number };

function toScreen(vp: Mat4, p: Vec3, W: number, H: number): { pt: Pt; w: number } {
  const x = vp[0]! * p[0] + vp[4]! * p[1] + vp[8]! * p[2] + vp[12]!;
  const y = vp[1]! * p[0] + vp[5]! * p[1] + vp[9]! * p[2] + vp[13]!;
  const w = vp[3]! * p[0] + vp[7]! * p[1] + vp[11]! * p[2] + vp[15]!;
  return { pt: { x: (x / w * 0.5 + 0.5) * W, y: (1 - (y / w * 0.5 + 0.5)) * H }, w };
}

export function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Pt[] = [], upper: Pt[] = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower.at(-2)!, lower.at(-1)!, p) <= 0) lower.pop(); lower.push(p); }
  for (const p of [...pts].reverse()) { while (upper.length >= 2 && cross(upper.at(-2)!, upper.at(-1)!, p) <= 0) upper.pop(); upper.push(p); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

export function pointInPolygon(p: Pt, poly: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!, b = poly[j]!;
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Project every drawable element of the active scene(s) at `timeMs`. Elements behind the camera are skipped. */
export function projectAll(project: Project, timeMs: number, measure: Measure): Projected[] {
  const W = project.width, H = project.height, out: Projected[] = [];
  for (const s of evaluate(project, timeMs).scenes) {
    const { view, proj } = cameraMatrices(W, H, s.camera);
    const vp = multiply(proj, view);
    for (const o of s.objects) {
      const t = o.t, el = o.element;
      const c: Vec3 = [t.x - W / 2, H / 2 - t.y, t.z];
      const m = modelMatrix(c[0], c[1], c[2], t.rotation_x, t.rotation_y, t.rotation, t.scale_x, t.scale_y, el.type === "mesh" ? t.scale_z : 1);
      const half: [number, number, number] = el.type === "mesh" ? [el.size / 2, el.size / 2, el.size / 2]
        : (() => { const q = measure(el); return [q.width / 2, q.height / 2, 0] as [number, number, number]; })();
      const local: Vec3[] = [];
      for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of el.type === "mesh" ? [-1, 1] : [0]) local.push([sx * half[0], sy * half[1], sz * half[2]]);
      const proj2 = local.map((p) => {
        const w: Vec3 = [m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!, m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!,
                         m[2]! * p[0] + m[6]! * p[1] + m[10]! * p[2] + m[14]!];
        return toScreen(vp, w, W, H);
      });
      if (proj2.some((q) => q.w <= 0)) continue;
      const cs = toScreen(vp, c, W, H), cx = toScreen(vp, [c[0] + 1, c[1], c[2]], W, H), cy = toScreen(vp, [c[0], c[1] + 1, c[2]], W, H);
      out.push({ id: o.id, hull: convexHull(proj2.map((q) => q.pt)), center: cs.pt, depth: cs.w,
        pxPerUnitX: Math.max(1e-6, Math.abs(cx.pt.x - cs.pt.x)), pxPerUnitY: Math.max(1e-6, Math.abs(cy.pt.y - cs.pt.y)) });
    }
  }
  return out;
}

/** Topmost (nearest to the camera) element under the point, or null. */
export function hitTest(items: Projected[], p: Pt): string | null {
  let best: Projected | null = null;
  for (const it of items) if (pointInPolygon(p, it.hull) && (!best || it.depth < best.depth)) best = it;
  return best?.id ?? null;
}

export const bounds = (poly: Pt[]) => {
  const xs = poly.map((p) => p.x), ys = poly.map((p) => p.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
};
